import axios, { AxiosError } from 'axios';
import { FieldValidationType } from '../enums/FieldValidationType';
import { ContactAddress } from '../types/ContactField';
import { sanitizeAllTags } from './sanitizeHtml';

/**
 * Creates a string from the property values of a contact address object, excluding "addressId" property
 * @param address
 */
export function stringifyContactAddress(address: ContactAddress | null | undefined): string {
    if (!address) return '';
    const keys = Object.keys(address) as (keyof ContactAddress)[];

    return keys
        .map(key => {
            return address[key] && address[key] !== 'addressId' ? `${address[key]}` : '';
        })
        .join(' ')
        .trim();
}

/**
 * sanitizeContactAddress
 *
 * @description
 * Removes all HTML tags from contact address fields
 *
 * @param address
 */
export function sanitizeContactAddress(address: ContactAddress): ContactAddress {
    let sanitizedAddress = {} as ContactAddress;

    for (let key in address) {
        sanitizedAddress = {
            ...sanitizedAddress,
            ...{
                [key as keyof ContactAddress]:
                    key !== 'addressId'
                        ? sanitizeAllTags(address[key as keyof ContactAddress]?.toString() || '')
                        : address[key as keyof ContactAddress],
            },
        };
    }

    return sanitizedAddress;
}

/**
 * parse contact field from an axios error object.
 * example object from api:
 * {"success":false,"return":null,"relatedId":2122,"error":"Phone number already in use."}
 */
export function parseContactFieldError(error: unknown): string | undefined {
    if (!error) return undefined;
    if (axios.isAxiosError(error)) {
        const axiosError = error;
        if (axiosError.response?.data?.errorMessage) {
            return axiosError.response.data.errorMessage;
        }
    }
    return 'An unknown error occurred.';
}

export function toFriendlyFieldValidationType(fieldValidationType: FieldValidationType): string {
    switch (fieldValidationType) {
        case FieldValidationType.address:
            return 'Address';
        case FieldValidationType.date:
            return 'Date';
        case FieldValidationType.dollars:
            return 'Dollar';
        case FieldValidationType.email:
            return 'Email';
        case FieldValidationType.other:
            return 'Other';
        case FieldValidationType.usMobilePhone:
            return 'Phone';
        case FieldValidationType.years:
            return 'Year';
        default:
            return 'Unknown';
    }
}
